import React, { useCallback, useEffect, useState } from "react";
import Spinner from "react-bootstrap/Spinner";
import { apiClient } from "../services/api/apiClient";
import { showCustomToast } from "./CustomToast";

// Superadmin billing controls for one organization. Everything here calls
// the /api/v1/sa/orgs/:id/billing/* endpoints; the org-admin side of
// billing lives in AdminPanel. Design: docs/billing-plan.md section 4.

export interface BillingState {
  entitled: boolean;
  reason: string;
  configured: boolean;
  enforcement: "off" | "log" | "enforce";
  status: string;
  override: "none" | "comp" | "suspended";
  overrideUntil: string | null;
  trialEndsAt: string | null;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  hasSubscription: boolean;
  systemCount: number;
  systemNames: string[];
  monthlyCents: number;
}

export interface Invoice {
  id: string;
  status: string;
  amountCents: number;
  currency: string;
  paidAt: string | null;
  hostedUrl: string | null;
  refundedCents: number | null;
  refundRequest?: RefundRequest | null;
  refundable?: boolean;
}

export interface RefundRequest {
  id: number;
  organization_id: number;
  stripe_invoice_id: string;
  amount_cents: number;
  paid_at: string;
  reason: string | null;
  status: "pending" | "approved" | "denied";
  created_at: string;
  subdomain?: string;
  friendlyname?: string;
}

type SaBilling = BillingState & { invoices: Invoice[]; refundRequests: RefundRequest[] };

export const money = (cents: number, currency = "usd") =>
  new Intl.NumberFormat("en-US", { style: "currency", currency }).format((cents || 0) / 100);

export const shortDate = (iso: string | null | undefined) =>
  iso ? new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—";

// One-line human label for the org's billing situation. Shared with the
// org list badge so the two never disagree.
export function describeBilling(s: {
  entitled: boolean;
  reason: string;
  status?: string;
  override?: string;
}): { label: string; color: string } {
  if (s.override === "suspended" || s.reason === "suspended") return { label: "Suspended", color: "#ef4444" };
  if (s.override === "comp" || s.reason === "comp") return { label: "Comped", color: "#8b5cf6" };
  switch (s.reason) {
    case "trial":
    case "trialing":
      return { label: "Trial", color: "#3b82f6" };
    case "active":
      return { label: "Active", color: "#10b981" };
    case "past_due":
      return { label: "Payment failed", color: "#ef4444" };
    case "canceled":
      return { label: "Canceled", color: "#ef4444" };
    case "unpaid":
      return { label: "Unpaid", color: "#ef4444" };
    case "no_subscription":
      return { label: "No subscription", color: "#f59e0b" };
    default:
      return { label: s.entitled ? "Active" : "Inactive", color: s.entitled ? "#10b981" : "#ef4444" };
  }
}

const card: React.CSSProperties = {
  background: "rgba(255, 255, 255, 0.95)",
  border: "1px solid rgba(33, 150, 243, 0.1)",
  borderRadius: "16px",
  padding: "20px 24px",
  marginBottom: "28px",
  boxShadow: "0 4px 16px rgba(0,0,0,0.04)",
};

const label: React.CSSProperties = {
  fontSize: "11px",
  color: "#64748b",
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  fontWeight: 600,
};

const btn = (bg: string, outline = false): React.CSSProperties => ({
  background: outline ? "transparent" : bg,
  color: outline ? bg : "white",
  border: `1px solid ${bg}`,
  borderRadius: "8px",
  padding: "7px 14px",
  fontSize: "13px",
  fontWeight: 600,
  cursor: "pointer",
});

const input: React.CSSProperties = {
  padding: "7px 10px",
  borderRadius: "8px",
  border: "1px solid #cbd5e1",
  fontSize: "13px",
};

const OrgBillingPanel: React.FC<{ orgId: number }> = ({ orgId }) => {
  const [data, setData] = useState<SaBilling | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [overrideUntil, setOverrideUntil] = useState("");
  const [overrideNote, setOverrideNote] = useState("");
  const [trialEnd, setTrialEnd] = useState("");

  const load = useCallback(async () => {
    const res = await apiClient.get(`/api/v1/sa/orgs/${orgId}/billing`);
    if (!res) return;
    const body = await res.json().catch(() => null);
    if (!res.ok || !body) {
      showCustomToast("Billing", body?.message || "Could not load billing", "error");
      return;
    }
    setData(body);
    setTrialEnd(body.trialEndsAt ? body.trialEndsAt.slice(0, 10) : "");
  }, [orgId]);

  useEffect(() => {
    setLoading(true);
    load().finally(() => setLoading(false));
  }, [load]);

  const act = async (path: string, payload: any, okMessage: string) => {
    setBusy(true);
    try {
      const res = await apiClient.post(`/api/v1/sa/orgs/${orgId}/billing/${path}`, payload);
      if (!res) return;
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        showCustomToast("Billing", body.message || "Request failed", "error");
        return;
      }
      showCustomToast("Billing", okMessage, "success");
      await load();
    } finally {
      setBusy(false);
    }
  };

  const decideRequest = async (r: RefundRequest, approve: boolean) => {
    if (approve && !window.confirm(`Refund ${money(r.amount_cents)} and cancel this org's subscription now?`)) return;
    setBusy(true);
    try {
      const res = await apiClient.post(`/api/v1/sa/refund-requests/${r.id}/decide`, { approve });
      if (!res) return;
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        showCustomToast("Refund", body.message || "Request failed", "error");
        return;
      }
      showCustomToast("Refund", approve ? "Refund issued" : "Request denied", "success");
      await load();
    } finally {
      setBusy(false);
    }
  };

  const refundInvoice = async (inv: Invoice) => {
    const amount = window.prompt(
      `Refund amount in dollars (max ${money(inv.amountCents)}). Leave as is for a full refund.`,
      (inv.amountCents / 100).toFixed(2),
    );
    if (amount === null) return;
    const cents = Math.round(Number(amount) * 100);
    if (!Number.isFinite(cents) || cents <= 0 || cents > inv.amountCents) {
      showCustomToast("Refund", "Enter a valid amount", "error");
      return;
    }
    const cancelNow = window.confirm("Also cancel the subscription immediately? OK = cancel, Cancel = keep it running.");
    await act("refund", { invoiceId: inv.id, amountCents: cents, cancelNow }, "Refund issued");
  };

  if (loading || !data) {
    return (
      <div style={{ ...card, textAlign: "center", color: "#64748b" }}>
        <Spinner animation="border" size="sm" /> Loading billing…
      </div>
    );
  }

  const d = describeBilling(data);
  const pending = data.refundRequests.filter((r) => r.status === "pending");

  return (
    <div style={card}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
        <h3 style={{ fontSize: "16px", fontWeight: 600, color: "#1e293b", margin: 0 }}>Billing</h3>
        <span
          style={{
            background: `${d.color}1a`,
            color: d.color,
            padding: "4px 12px",
            borderRadius: "999px",
            fontSize: "12px",
            fontWeight: 700,
          }}
        >
          {d.label}
        </span>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: "16px", marginBottom: "20px" }}>
        <div>
          <div style={{ fontSize: "20px", fontWeight: 700, color: "#1e293b" }}>{data.systemCount}</div>
          <div style={label}>Billed systems</div>
        </div>
        <div>
          <div style={{ fontSize: "20px", fontWeight: 700, color: "#1e293b" }}>{money(data.monthlyCents)}</div>
          <div style={label}>Per month</div>
        </div>
        <div>
          <div style={{ fontSize: "20px", fontWeight: 700, color: "#1e293b" }}>{shortDate(data.trialEndsAt)}</div>
          <div style={label}>Trial ends</div>
        </div>
        <div>
          <div style={{ fontSize: "20px", fontWeight: 700, color: "#1e293b" }}>{shortDate(data.currentPeriodEnd)}</div>
          <div style={label}>{data.cancelAtPeriodEnd ? "Cancels on" : "Renews"}</div>
        </div>
        <div>
          <div style={{ fontSize: "20px", fontWeight: 700, color: data.enforcement === "enforce" ? "#ef4444" : "#94a3b8" }}>
            {data.enforcement}
          </div>
          <div style={label}>Enforcement</div>
        </div>
      </div>

      {data.override !== "none" && (
        <div style={{ fontSize: "13px", color: "#475569", marginBottom: "16px" }}>
          Override <strong>{data.override}</strong>
          {data.overrideUntil ? ` until ${shortDate(data.overrideUntil)}` : " with no end date"}.
        </div>
      )}

      {/* Override controls */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: "10px", alignItems: "center", marginBottom: "14px" }}>
        <span style={{ ...label, marginRight: "4px" }}>Override</span>
        <input type="date" value={overrideUntil} onChange={(e) => setOverrideUntil(e.target.value)} style={input} title="Optional end date" />
        <input
          value={overrideNote}
          onChange={(e) => setOverrideNote(e.target.value)}
          placeholder="Note (optional)"
          style={{ ...input, minWidth: "180px" }}
        />
        <button
          disabled={busy}
          style={btn("#8b5cf6")}
          onClick={() => act("override", { override: "comp", until: overrideUntil || null, note: overrideNote }, "Org comped")}
        >
          Comp
        </button>
        <button
          disabled={busy}
          style={btn("#ef4444")}
          onClick={() =>
            window.confirm("Suspend this org? Every system will be disconnected immediately.") &&
            act("override", { override: "suspended", until: overrideUntil || null, note: overrideNote }, "Org suspended")
          }
        >
          Suspend
        </button>
        {data.override !== "none" && (
          <button disabled={busy} style={btn("#64748b", true)} onClick={() => act("override", { override: "none" }, "Override cleared")}>
            Clear override
          </button>
        )}
      </div>

      {/* Trial + cancel */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: "10px", alignItems: "center", marginBottom: "20px" }}>
        <span style={{ ...label, marginRight: "4px" }}>Trial ends</span>
        <input type="date" value={trialEnd} onChange={(e) => setTrialEnd(e.target.value)} style={input} />
        <button
          disabled={busy || !trialEnd}
          style={btn("#3b82f6")}
          onClick={() => act("trial", { trialEndsAt: new Date(`${trialEnd}T23:59:59`).toISOString() }, "Trial updated")}
        >
          Set trial end
        </button>
        {data.hasSubscription && (
          <>
            <span style={{ flex: 1 }} />
            <button
              disabled={busy || data.cancelAtPeriodEnd}
              style={btn("#f59e0b", true)}
              onClick={() => act("cancel", { immediately: false }, "Subscription will end at period end")}
            >
              Cancel at period end
            </button>
            <button
              disabled={busy}
              style={btn("#ef4444", true)}
              onClick={() =>
                window.confirm("Cancel immediately? Systems disconnect now and the org must subscribe again.") &&
                act("cancel", { immediately: true }, "Subscription canceled")
              }
            >
              Cancel now
            </button>
          </>
        )}
      </div>

      {pending.length > 0 && (
        <>
          <div style={{ ...label, marginBottom: "8px" }}>Refund requests</div>
          {pending.map((r) => (
            <div
              key={r.id}
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "10px",
                alignItems: "center",
                padding: "10px 12px",
                background: "rgba(245,158,11,0.08)",
                border: "1px solid rgba(245,158,11,0.3)",
                borderRadius: "10px",
                marginBottom: "8px",
                fontSize: "13px",
              }}
            >
              <strong>{money(r.amount_cents)}</strong>
              <span style={{ color: "#64748b" }}>paid {shortDate(r.paid_at)}, requested {shortDate(r.created_at)}</span>
              {r.reason && <span style={{ color: "#475569" }}>“{r.reason}”</span>}
              <span style={{ flex: 1 }} />
              <button disabled={busy} style={btn("#10b981")} onClick={() => decideRequest(r, true)}>
                Approve and refund
              </button>
              <button disabled={busy} style={btn("#64748b", true)} onClick={() => decideRequest(r, false)}>
                Deny
              </button>
            </div>
          ))}
        </>
      )}

      <div style={{ ...label, margin: "12px 0 8px" }}>Invoices</div>
      {data.invoices.length === 0 ? (
        <div style={{ fontSize: "13px", color: "#94a3b8" }}>No invoices yet.</div>
      ) : (
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
          <tbody>
            {data.invoices.map((inv) => (
              <tr key={inv.id} style={{ borderTop: "1px solid #f1f5f9" }}>
                <td style={{ padding: "8px 4px", color: "#64748b" }}>{shortDate(inv.paidAt)}</td>
                <td style={{ padding: "8px 4px", fontWeight: 600 }}>{money(inv.amountCents, inv.currency)}</td>
                <td style={{ padding: "8px 4px", color: "#64748b" }}>
                  {inv.status}
                  {inv.refundedCents ? ` · refunded ${money(inv.refundedCents, inv.currency)}` : ""}
                </td>
                <td style={{ padding: "8px 4px", textAlign: "right" }}>
                  {inv.hostedUrl && (
                    <a href={inv.hostedUrl} target="_blank" rel="noreferrer" style={{ marginRight: "12px", fontSize: "12px" }}>
                      View
                    </a>
                  )}
                  {inv.status === "paid" && !inv.refundedCents && (
                    <button disabled={busy} style={btn("#64748b", true)} onClick={() => refundInvoice(inv)}>
                      Refund
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default OrgBillingPanel;
